import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TablePagination,
  Drawer,
  Breadcrumbs,
  Chip,
  Tooltip,
} from "@mui/material";
import { tooltipStyleProps } from "services/constants/Styles";
import { useSelector } from "react-redux";
import { customGetAPI } from "services/utils/CustomAPI";
import { Loader } from "components/Loader";
import { InfoMessage } from "components/CustomComponents/CustomButton";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { themes, tableRowSize, theme, drawerStyle } from "services/constants";

import { ThemeProvider } from "@mui/material/styles";
import EODViewPanel from "pages/MyZone/EODReport/EODViewPanel";
import { FilterProvider } from "pages/FilterContext";
import {
  DateRange,
  Comment,
  Assessment,
  Person,
  ModeStandby,
  AccessTime,
} from "@mui/icons-material";

// Team Zone Headers: Added Emp Name and Comments, Removed Actions (since row is clickable)
const TeamEODReportHeaders = [
  {
    key: "report_date",
    name: "Date",
    type: "date",
    icon: <DateRange fontSize="small" />,
    showBetweenOperator: true,
    dateRangeFields: {
      fromField: "report_date",
      toField: "report_date",
    },
  },
  {
    key: "user_name",
    name: "Emp Name",
    type: "string",
    icon: <Person fontSize="small" />,
  },
  {
    key: "total_worked_hours",
    name: "Hours",
    type: "time",
    icon: <AccessTime fontSize="small" />,
    showBetweenOperator: false,
  },
  {
    key: "achieved_tasks_summary",
    name: "Achieved Today",
    type: "string",
    icon: <Assessment fontSize="small" />,
  },
  {
    key: "planned_tasks_summary",
    name: "Planned for Next",
    type: "string",
    icon: <Assessment fontSize="small" />,
  },
  {
    key: "blockers_notes",
    name: "Blockers",
    type: "string",
    icon: <Assessment fontSize="small" />,
  },
  {
    key: "status",
    name: "Status",
    type: "string",
    icon: <ModeStandby fontSize="small" />,
  },
  {
    key: "approver_comments",
    name: "Comments",
    type: "string",
    icon: <Comment fontSize="small" />,
  },
];

const TeamEODContent = () => {
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);

  // State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [teamReports, setTeamReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);

  // Store selected Report ID for the View Panel API call
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [isViewPanelOpen, setIsViewPanelOpen] = useState(false);
  const containerRef = useRef(null);

  // --- Fetch Team Reports ---
  const fetchTeamReports = useCallback(async () => {
    setLoading(true);
    try {
      const response = await customGetAPI(
        `${domain}eod-reports/?user_id=${userDetails?.id}`,
      );
      if (response.ok) {
        const reports = Array.isArray(response.responseDetails)
          ? response.responseDetails
          : response.responseDetails?.data || [];

        // 1. Filter out Drafts as per requirement
        const submittedReports = reports.filter((r) => r.status !== "Draft");

        setTeamReports(submittedReports);
        setFilteredReports(submittedReports);
      }
    } catch (error) {
      console.error("Fetch error", error);
    } finally {
      setLoading(false);
    }
  }, [domain, userDetails]);

  useEffect(() => {
    fetchTeamReports();
  }, [fetchTeamReports]);

  // --- Handlers ---
  const reCallApi = (shouldRefetch) => {
    if (shouldRefetch) fetchTeamReports();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setFilteredReports(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  const handleViewPanel = (row) => {
    if (row?.id) {
      setSelectedReportId(row.id);
      setIsViewPanelOpen(true);
    }
  };

  const handleViewClosePanel = () => {
    setIsViewPanelOpen(false);
    setSelectedReportId(null);
  };

  // UI Helper
  const renderTaskList = (tasks) => {
    if (!tasks) return "-";
    const list = Array.isArray(tasks) ? tasks : [tasks];
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {list.slice(0, 3).map((task, index) => (
          <Typography
            key={index}
            variant="body2"
            sx={{ textAlign: "left", lineHeight: 1.3, fontSize: "0.8rem" }}
          >
            • {task}
          </Typography>
        ))}
        {list.length > 3 && (
          <Typography variant="caption" color="primary">
            +{list.length - 3} more
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      {loading ? (
        <Loader />
      ) : (
        <React.Fragment>
          <Box>
            <Box sx={{ mb: 3, mt: 2 }}>
              <Breadcrumbs aria-label="breadcrumb" separator="›" sx={{ mb: 2 }}>
                <Typography>Team Zone</Typography>
                <Typography color={themes?.blueColor}>EOD Reports</Typography>
              </Breadcrumbs>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Team EOD Reports
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Track and manage daily reports submitted by your team members to
                stay updated on their progress.
              </Typography>
            </Box>

            <Paper sx={{ width: "100%" }}>
              <CustomFilter
                title="Team Reports"
                filterHeaders={TeamEODReportHeaders}
                data={teamReports}
                filteredData={filteredReports}
                onFilteredDataChange={handleFilteredDataChange}
                screenAccess={true}
              />

              <Box sx={{ ml: 2, mr: 2 }}>
                <TableContainer ref={containerRef} sx={{ maxHeight: "60vh" }}>
                  <Table className="border">
                    <TableHead
                      sx={{
                        bgcolor: themes.primary,
                        position: "sticky",
                        top: -1,
                        zIndex: 10,
                      }}
                    >
                      <TableRow sx={tableRowSize}>
                        {TeamEODReportHeaders.map((header) => (
                          <TableCell
                            key={header.key}
                            align="center"
                            sx={{
                              color: themes.headLine,
                              fontWeight: "bold",
                              fontSize: "0.8rem",
                              border: "1px solid #ddd",
                            }}
                          >
                            {header.name}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredReports?.length === 0 ? (
                        <InfoMessage dataLength={teamReports} />
                      ) : (
                        filteredReports
                          ?.slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                          )
                          ?.map((row) => {
                            const clickable =
                              row.status !== "Weekoff" &&
                              row.status !== "Holiday" &&
                              row.status !== "Leave";
                            return (
                              <TableRow
                                key={row.id}
                                sx={{
                                  ...tableRowSize,
                                  "&:hover": clickable
                                    ? { backgroundColor: "#f5f5f5" }
                                    : {},
                                  cursor: clickable ? "pointer" : "default",
                                  opacity: clickable ? 1 : 0.6,
                                }}
                                onClick={() => {
                                  if (clickable) handleViewPanel(row);
                                }}
                              >
                                <TableCell
                                  align="left"
                                  sx={{ border: "1px solid #ddd" }}
                                >
                                  {row.report_date}
                                </TableCell>
                                <TableCell
                                  align="left"
                                  sx={{
                                    border: "1px solid #ddd",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {row.user_name || row.emp_name}
                                </TableCell>
                                <TableCell
                                  align="left"
                                  sx={{
                                    border: "1px solid #ddd",
                                  }}
                                >
                                  {row.total_worked_hours || "-"}
                                </TableCell>
                                <TableCell
                                  align="left"
                                  sx={{
                                    border: "1px solid #ddd",
                                    maxWidth: 300,
                                  }}
                                >
                                  {renderTaskList(row.achieved_tasks_summary)}
                                </TableCell>
                                <TableCell
                                  align="left"
                                  sx={{
                                    border: "1px solid #ddd",
                                    maxWidth: 300,
                                  }}
                                >
                                  {renderTaskList(row.planned_tasks_summary)}
                                </TableCell>
                                <TableCell
                                  align="left"
                                  sx={{ border: "1px solid #ddd" }}
                                >
                                  {row.blockers_notes || "-"}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ border: "1px solid #ddd" }}
                                >
                                  <Chip
                                    label={row.status}
                                    size="small"
                                    sx={{
                                      bgcolor:
                                        row.status === "Submitted"
                                          ? "#fff3e0"
                                          : row.status === "Approved"
                                            ? "#e8f5e9"
                                            : row.status === "Weekoff"
                                              ? "#e0e0e0"
                                              : row.status === "Holiday"
                                                ? "#9D97FA"
                                                : row.status === "Leave"
                                                  ? "#D3A50E"
                                                  : "#FFF3E0",
                                      color:
                                        row.status === "Submitted"
                                          ? "#ef6c00"
                                          : row.status === "Approved"
                                            ? "#2e7d32"
                                            : row.status === "Weekoff"
                                              ? "#242020"
                                              : row.status === "Holiday"
                                                ? "#000000"
                                                : row.status === "Leave"
                                                  ? "#000000"
                                                  : "#f58769",
                                      fontWeight: 600,
                                      minWidth: 80,
                                    }}
                                  />
                                </TableCell>
                                <TableCell
                                  align="left"
                                  sx={{
                                    border: "1px solid #ddd",
                                    maxWidth: 150,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  <Tooltip
                                    title={row.approver_comments || ""}
                                    arrow
                                    slotProps={tooltipStyleProps}
                                  >
                                    <span>{row.approver_comments || "-"}</span>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              <Box sx={{ mt: 1 }}>
                {filteredReports?.length > 25 && (
                  <TablePagination
                    className="custom-pagination"
                    component="div"
                    rowsPerPageOptions={[25, 50, 75, 100]}
                    count={filteredReports?.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                )}
              </Box>
            </Paper>
          </Box>

          <Drawer
            anchor="right"
            open={isViewPanelOpen}
            onClose={(event, reason) => {
              if (reason !== "backdropClick") handleViewClosePanel();
            }}
            sx={drawerStyle}
          >
            {isViewPanelOpen && (
              <EODViewPanel
                onClose={handleViewClosePanel}
                reportId={selectedReportId}
                reCallApi={reCallApi}
                hasProjectAccess={true}
                viewMode="team_zone" // Pass Team Mode flag
              />
            )}
          </Drawer>
        </React.Fragment>
      )}
    </ThemeProvider>
  );
};

export const TeamEODReports = () => {
  const { domain, userDetails, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  const eodModule = userScreenAccess?.module_details.find(
    (mod) => mod.module_name === "EOD Report",
  );

  const subModuleId = eodModule?.submodules?.find(
    (sub) => sub.sub_module_name === "EOD Report",
  )?.sub_module_id;
  return (
    <FilterProvider
      domain={domain}
      userDetails={userDetails}
      subModuleId={subModuleId}
    >
      <TeamEODContent />
    </FilterProvider>
  );
};

export default TeamEODReports;
